import { type Ref, computed, reactive, toRef, watch } from 'vue'
import { useUserStore } from '~/stores/user'
import { useLicensePlateRules } from '~/compositions/useLicensePlateRules'
import {
  TERMINALS_REQUIRING_MASTER_BL,
  TERMINALS_WITH_OWN_CHASSIS_OPTION_FOR_IMPORT_APPT,
} from '~/constants'
import type { Container } from '~/models/containers'
import type { AppointmentSlot } from '~/models/appointmentSlot'
import type { GETAutoBookRequest, TerminalName } from '~/services/apiClient'

import { AutoBookRequestStatus, PortalName } from '~/services/apiClient'
import type { BookingFormValues } from '~/components/inputs/bookingTypes'

export function useContainerBooking({
  container,
  appointmentSlot,
  existingSettings,
}: {
  container: Container
  appointmentSlot?: AppointmentSlot
  existingSettings: Ref<GETAutoBookRequest | undefined>
}) {
  const userStore = useUserStore()

  const terminal = toRef(container, 'terminal') as Ref<TerminalName | undefined>

  const existingAppointment = toRef(container, 'booked_appointment')

  const licensePlateRules = useLicensePlateRules({
    terminal,
    appointmentWindowStart: computed(() => appointmentSlot?.window_start),
    forEmptyBooking: false,
    existingAppointment,
  })
  const {
    usesLicensePlate,
    requiresLicensePlate,
    shouldPrefillLicensePlate,
    shouldBeDisabled: licensePlateShouldBeDisabled,
  } = licensePlateRules

  const requiresOwnChassisSelector = computed(() =>
    terminal.value
      ? TERMINALS_WITH_OWN_CHASSIS_OPTION_FOR_IMPORT_APPT.has(terminal.value)
      : false
  )

  const ownChassis = ref(
    existingSettings.value?.settings.own_chassis ?? userStore.useOwnChassis
  )
  const form = reactive<BookingFormValues>({
    containerNumber: container.number,
    masterBL: '',
    licensePlate: '',
    ownChassis: undefined,
  })

  function prefillMasterBL() {
    const settings = existingSettings.value?.settings
    if (
      settings?.master_bill_of_lading &&
      container.number === existingSettings.value?.container_number
    ) {
      form.masterBL = settings.master_bill_of_lading
      return
    }
    if (container.import_status?.master_bill_of_lading) {
      form.masterBL = container.import_status.master_bill_of_lading
      return
    }
    if (container.import_statuses && container.import_statuses.length > 0) {
      for (const importStatus of container.import_statuses) {
        if (
          importStatus.master_bill_of_lading &&
          importStatus.source !== PortalName.PortOptimizer
        ) {
          form.masterBL = importStatus.master_bill_of_lading
          return
        }
      }
    }
    if (container.master_bill_of_lading) {
      form.masterBL = container.master_bill_of_lading
    }
  }

  function prefillLicensePlate() {
    const settings = existingSettings.value?.settings
    if (!usesLicensePlate.value) {
      form.licensePlate = ''
    } else if (
      settings?.license_plate &&
      container.number === existingSettings.value?.container_number
    ) {
      form.licensePlate = settings.license_plate
    } else if (container.booked_appointment?.truck_license_plate_number) {
      form.licensePlate =
        container.booked_appointment.truck_license_plate_number
    }
  }

  function prefillOwnChassis() {
    if (requiresOwnChassisSelector.value && existingSettings.value?.settings) {
      ownChassis.value = existingSettings.value.settings.own_chassis || false
    }
  }

  watch(
    existingSettings,
    () => {
      prefillMasterBL()
      prefillLicensePlate()
      prefillOwnChassis()
    },
    { immediate: true }
  )

  watch(
    () => container.booked_appointment?.truck_license_plate_number,
    () => {
      prefillLicensePlate()
    },
    { immediate: true }
  )
  const requiresMasterBL = computed(
    () =>
      container.terminal != null &&
      TERMINALS_REQUIRING_MASTER_BL.has(container.terminal)
  )
  const rules = computed(() => ({
    masterBL: [
      {
        required: requiresMasterBL.value,
        message: 'Required',
        trigger: 'change',
      },
    ],
    licensePlate: [
      {
        required: requiresLicensePlate.value,
        message: 'Required',
        trigger: 'change',
      },
    ],
  }))

  const statusColor = computed(() => {
    switch (container.auto_book_request_status) {
      case AutoBookRequestStatus.Requested:
        return 'green'
      case AutoBookRequestStatus.Complete:
        return 'gray'
      case AutoBookRequestStatus.Failed:
        return 'red'
      case AutoBookRequestStatus.InProgress:
        return 'yellow'
      case AutoBookRequestStatus.Cancelled:
        return 'grey'
      default:
        return 'black'
    }
  })

  function getValues() {
    return {
      masterBL: form.masterBL,
      licensePlate: form.licensePlate,
      ownChassis: requiresOwnChassisSelector.value
        ? ownChassis.value
        : undefined,
    }
  }

  return {
    form,
    rules,
    ownChassis,
    statusColor,
    requiresOwnChassisSelector,
    requiresMasterBL,
    usesLicensePlate,
    requiresLicensePlate,
    shouldPrefillLicensePlate,
    licensePlateShouldBeDisabled,
    getValues,
  }
}
